// libraries
import React, { useState, useEffect } from 'react'
import clsx from 'clsx'

// components
import Section from '/react4xp/components/Section'
import SectionDivider from '/react4xp/components/SectionDivider'
import RichText from '/react4xp/components/RichText'
import Image from '/react4xp/components/Image'
import Video from '/react4xp/components/Video'

// utils
import { isNonWhitespaceString } from '/lib/reactutils/utils.js'

// svg
import { GeneralClock, GeneralMilk, GeneralNut } from '/react4xp/components/Svg/Icons/General'
import { UxAngleDown } from '/react4xp/components/Svg/Ux/Angle'

// prop types
export interface ProductPageWrapperProps {
    topPart?: {
		bgColor?: 'mini' | string
		whiteText?: boolean
		image?: {
			src?: string
			srcMedium?: string
			alt?: string
		}
		title?: string
		desc?: string
	}
	nutritionFacts?: {
		sectionTitle?: string
		items?: {
			title?: string
			value?: string
		}[]
		footNote?: string
	}[]
	ingredients?: {
		sectionTitle?: string
		content?: string
	}
	allergens?: {
		allergensTitle?: string
		allergensItems?: {
			icon?: string
			title?: string
		}[]
		tracesOfTitle?: string
		tracesOfItems?: {
			icon?: string
			title?: string
		}[]
	}
	shops?: {
		sectionTitle?: string
		stores?: {
			title?: string
			storeAt?: {
				title?: string
				items?: string[]
			}
			productAt?: {
				title?: string
				items?: string[]
			}
		}[]
	}
}

export default function ProductPageWrapper({
	topPart,
	nutritionFacts,
	ingredients,
	allergens,
	shops
}: ProductPageWrapperProps) {

	// custom bg color
    const customBgColor = topPart?.bgColor && !['mini'].includes(topPart?.bgColor) ? { color: topPart?.bgColor } : {}

	// toggle all details blocks
	const [isNutritionFactsActive, setIsNutritionFactsActive] = useState(false)
	const toggleNutritionFacts = () => { setIsNutritionFactsActive((prev) => !prev) }

	const [isIngredientsActive, setIsIngredientsActive] = useState(false)
	const toggleIngredients = () => { setIsIngredientsActive((prev) => !prev) }

	const [isAllergensActive, setIsAllergensActive] = useState(false)
	const toggleAllergens = () => { setIsAllergensActive((prev) => !prev) }

	const [isShopsActive, setIsShopsActive] = useState(false)
	const toggleShops = () => { setIsShopsActive((prev) => !prev) }

	// change the accordion to open / closed based on the window width
	const [lastWidth, setLastWidth] = useState(null)

	useEffect(() => {
        const checkWindowWidth = () => {
            const currentWidth = window.innerWidth

            if (currentWidth !== lastWidth) {
                setLastWidth(currentWidth)
                const isWideScreen = currentWidth >= 768

				setIsNutritionFactsActive(isWideScreen)
                setIsIngredientsActive(isWideScreen)
                setIsAllergensActive(isWideScreen)
				setIsShopsActive(isWideScreen)
            }
        }

        checkWindowWidth()

        window.addEventListener('resize', checkWindowWidth)

        return () => {
            window.removeEventListener('resize', checkWindowWidth)
        }
    }, [lastWidth])

	// update tabindex based on the window width
	const [tabIndex, setTabIndex] = useState(null)

	useEffect(() => {
		const updateTabIndex = () => {
			if (window.innerWidth >= 768) {
				setTabIndex(-1)
			} else {
				setTabIndex(null)
			}
		}

		updateTabIndex()

		window.addEventListener('resize', updateTabIndex)

		return () => window.removeEventListener('resize', updateTabIndex)
	}, [])

	return (
		<div className='page-product'>

			<Section
				bgColor={topPart?.bgColor}
				whiteText={topPart?.whiteText}
				className={clsx(
					'page-product__header',
					topPart?.bgColor && 'page-product__header--has-bg-color'
				)}
				marginTop='none'
				marginBottom='none'
			>
				<div className='container'>
					<div className='row'>

						{topPart?.image?.src && (
							<div className='col-md-6'>
								<div className='page-product__header__image-wrapper'>
									<Image
										src={topPart?.image?.src} // 700px x 700px
										srcMedium={topPart?.image?.srcMedium} // 550px x 500px
										width={700}
										alt={topPart?.image?.alt}
										sizes='
											(max-width: 768px) 100vw,
											50vw
										'
										className='cover'
									/>
								</div>
							</div>
						)}

						<div className={clsx(
                            'page-product__header__texts-wrapper',
                            topPart?.image?.src ? 'col-md-6' : 'col-12 page-product__header__texts-wrapper--no-image'
                        )}>

                            {isNonWhitespaceString(topPart?.title) && (
                                <h1 className='h1-hero'>
                                    {topPart?.title}
                                </h1>
                            )}

                            {isNonWhitespaceString(topPart?.desc) && (
                                <div
									className='h3'
									dangerouslySetInnerHTML={{ __html: topPart?.desc }}
									suppressHydrationWarning
								/>
                            )}

                        </div>

					</div>
				</div>
			</Section>

			{topPart?.bgColor && (
                <svg
                    width='1440'
                    height='14'
                    viewBox='0 0 1440 14'
                    className={clsx(
                        'page-product__header-divider',
                        topPart?.bgColor === 'mini' && 'mini'
                    )}
                    xmlns='http://www.w3.org/2000/svg'
                    style={customBgColor}
                >
                    <path d='M1246.57 13.5451C1255.29 13.3297 1440 2.54519 1440 2.54519V0L0 0.000125933L2.22433e-07 2.54532L311.392 13.5452C311.392 13.5452 638.12 1.42504 846.432 2.8297C1003.42 3.88829 1237.84 13.7606 1246.57 13.5451Z' fill='currentColor'/>
                </svg>            
            )}

			{!topPart?.bgColor && (
				<SectionDivider className='page-product__divider' />
			)}

			<Section
				className='page-product__details'
				marginTop='small'
				marginBottom='small'
			>
				<div className='container'>

					<div className='row'>

						{nutritionFacts?.length > 0 && (
							<div className='col-md-6 left'>
								<div className='left-inner'>

									<div className={clsx(
										'accordion',
										isNutritionFactsActive && 'is-active'
									)}>

										<button
											type='button'
											className='accordion__title'
											onClick={toggleNutritionFacts}
											tabIndex={tabIndex}
										>

											<h3 className='products-box__title h3'>
												{nutritionFacts[0]?.sectionTitle}
											</h3>

											<UxAngleDown />

										</button>

										<div className='accordion__content'>
											<div>
												
												{nutritionFacts[0]?.items && (
													<div className='tags-block h5'>

														{nutritionFacts[0]?.items?.map((item, i) => {

															const isSubItem = item?.title?.toLowerCase().trim() === 'hvorav mettede fettsyrer' || item?.title?.toLowerCase().trim() === 'hvorav sukkerarter'

															return (
																<div key={i} className={clsx(isSubItem && 'tags-block__italic')}>
																	<p>{item?.title}</p>
																	<p>{item?.value}</p>
																</div>
															)
														})}

														{nutritionFacts[0]?.footNote && (
															<div className='foot-note'>
																<p>
																	{nutritionFacts[0]?.footNote}
																</p>
															</div>
														)}

													</div>
												)}

												{nutritionFacts.slice(1).map((item, i) => (
													<div key={i} className='accordion__content__extra'>
														
														<h3 className='products-box__title h3'>
															{item?.sectionTitle}
														</h3>

														<div className='tags-block h5'>
															{item?.items?.map((subItem, i2) => {

																const isSubItem = subItem?.title?.toLowerCase().trim() === 'hvorav mettede fettsyrer' || subItem?.title?.toLowerCase().trim() === 'hvorav sukkerarter'

																return (
																	<div key={i2} className={clsx(isSubItem && 'tags-block__italic')}>
																		<p>{subItem?.title}</p>
																		<p>{subItem?.value}</p>
																	</div>
																)
															})}

															{item?.footNote && (
																<div className='foot-note'>
																	<p>
																		{item?.footNote}
																	</p>
																</div>
															)}
														</div>

													</div>
												))}

											</div>
										</div>

									</div>
								</div>
							</div>
						)}

						<div className={clsx(
							'right',
							nutritionFacts ? 'col-md-6' : 'col-12'
						)}>

							{ingredients && (
								<div className={clsx(
									'accordion',
									isIngredientsActive && 'is-active'
								)}>

									<button
										type='button'
										className='accordion__title'
										onClick={toggleIngredients}
										tabIndex={tabIndex}
									>

										<h3 className='products-box__title h3'>
											{ingredients?.sectionTitle}
										</h3>

										<UxAngleDown />

									</button>

									<div className='accordion__content'>
										<div>
											<RichText content={ingredients?.content} />
										</div>
									</div>

								</div>
							)}

							{(allergens?.allergensItems?.length > 0) || (allergens?.tracesOfItems?.length > 0) && (
								<div className={clsx(
									'accordion',
									isAllergensActive && 'is-active'
								)}>

									<button
										type='button'
										className='accordion__title'
										onClick={toggleAllergens}
										tabIndex={tabIndex}
									>

										<h3 className='products-box__title h3'>
											{allergens?.allergensTitle}
										</h3>

										<UxAngleDown />

									</button>

									<div className='accordion__content'>
										<div>

											{(allergens?.allergensItems?.length > 0) &&
												<div className='tags-block text-small'>
													{allergens?.allergensItems?.map((item, i) =>
														<div
															className='shape-mask'
															key={i}
														>
															{item.icon && (
																<span
																	dangerouslySetInnerHTML={{ __html: item.icon }}
																	suppressHydrationWarning
																/>
															)}
															<p>{item.title}</p>
														</div>
													)}
												</div>
											}

											{(allergens?.tracesOfItems?.length > 0) &&
												<>
													<div className='accordion__title'>
														<h3 className='products-box__title h4'>
															{allergens?.tracesOfTitle}
														</h3>
													</div>

													<div className='tags-block text-small'>
														{allergens?.tracesOfItems?.map((item, i) =>
															<div
																className='shape-mask'
																key={i}
															>
																{item.icon && (
																	<span
																		dangerouslySetInnerHTML={{ __html: item.icon }}
																		suppressHydrationWarning
																	/>
																)}
																<p>{item.title}</p>
															</div>
														)}
													</div>
												</>
											}
										</div>
									</div>

								</div>
							)}

						</div>

					</div>

					{(shops?.stores?.length > 0) && (
                        <div className='page-product__stores'>
							<div className={clsx(
								'accordion',
								isShopsActive && 'is-active'
							)}>

								<button
									type='button'
									className='accordion__title'
									onClick={toggleShops}
									tabIndex={tabIndex}
								>

									<h3 className='products-box__title h3'>
										{shops?.sectionTitle}
									</h3>

									<UxAngleDown />

								</button>

								<div className='accordion__content'>
									<div>

										{shops?.stores?.length === 1 && (
											<div className='grid grid--1-col'>

												{(shops?.stores?.[0]?.storeAt?.items?.length > 0) && (
													<div className='flex-inner'>

														{shops?.stores?.[0]?.title && (
															<p>
																<b>{shops?.stores?.[0]?.title}</b>
															</p>
														)}

														{shops?.stores?.[0]?.storeAt?.title && (
															<p>
																{shops?.stores?.[0]?.storeAt?.title}
															</p>
														)}

														<div className='tags-block text-small'>
															{shops?.stores?.[0]?.storeAt?.items.map((item, i) => (
																<div className='shape-mask' key={i}>
																	<p>
																		{item}
																	</p>
																</div>
															))}

														</div>

													</div>
												)}

												{(shops?.stores?.[0]?.productAt?.items?.length > 0) && (
													<div className='flex-inner'>

														{shops?.stores?.[0]?.productAt?.title && (
															<p>
																{shops?.stores?.[0]?.productAt?.title}
															</p>
														)}

														<div className='tags-block text-small'>
															{shops?.stores?.[0]?.productAt?.items.map((item, i) => (
																<div className='shape-mask' key={i}>
																	<p>
																		{item}
																	</p>
																</div>
															))}
														</div>

													</div>
												)}

											</div>
										)}

										{shops?.stores?.length === 2 && (
											<div className='grid grid--2-cols'>
												{shops?.stores?.map((section, i) => (
													<div key={i} className='flex-inner'>

														
														{section.title && (
															<p>
																<b>{section.title}</b>
															</p>
														)}

														{(section.storeAt?.items?.length > 0) && (
															<>
																{section.storeAt?.title && (
																	<p>
																		{section.storeAt?.title}
																	</p>
																)}

																<div className='tags-block text-small'>

																	{section.storeAt?.items.map((item, i2) => (
																		<div className='shape-mask' key={i2}>
																			<p>
																				{item}
																			</p>
																		</div>
																	))}

																</div>
															</>
														)}

														{(section.productAt?.items?.length > 0) && (
															<>
																{section.productAt?.title && (
																	<p>
																		{section.productAt?.title}
																	</p>
																)}

																<div className='tags-block text-small'>

																	{section.productAt?.items.map((item, i2) => (
																		<div className='shape-mask' key={i2}>
																			<p>
																				{item}
																			</p>
																		</div>
																	))}

																</div>
															</>
														)}

													</div>
												))}
											</div>
										)}

										{shops?.stores?.length === 3 && (
											<div className='grid grid--3-cols'>
												{shops?.stores?.map((section, i) => (
													<div key={i} className='flex-inner'>

														{section.title && (
															<p>
																<b>{section.title}</b>
															</p>
														)}

														{(section.storeAt?.items?.length > 0) && (
															<>
																{section.storeAt?.title && (
																	<p>
																		{section.storeAt?.title}
																	</p>
																)}

																<div className='tags-block text-small'>
																	{section.storeAt?.items.map((item, i2) => (
																		<div className='shape-mask' key={i2}>
																			<p>
																				{item}
																			</p>
																		</div>
																	))}
																</div>
															</>
														)}

														{(section.productAt?.items?.length > 0) && (
															<>
																{section.productAt?.title && (
																	<p>
																		{section.productAt?.title}
																	</p>
																)}

																<div className='tags-block text-small'>
																	{section.productAt?.items.map((item, i2) => (
																		<div className='shape-mask' key={i2}>
																			<p>
																				{item}
																			</p>
																		</div>
																	))}
																</div>
															</>
														)}

													</div>
												))}
											</div>
										)}

									</div>
								</div>

							</div>
                        </div>
                    )}

				</div>
			</Section>

		</div>
	)
}
